import { useContext, useEffect } from 'react';
import GlobalContext from '../context/store';
import LegalDocumentLayout, {
  LegalIntro,
  LegalList,
  LegalP,
  LegalSection,
  LegalStrong,
} from '../components/legal/LegalDocumentLayout'

export default function PrivacyPolicyPage() {
  const { company = {} } = useContext(GlobalContext) || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const companyName = company.name || 'Dollhouse Mermaid';
  const companyEmail = company.email || 'info@dollhousemermaid.com';
  const companyUrl = company.urls?.[0] || window.location.hostname;
  return (
    <LegalDocumentLayout title="Privacy Policy" lastUpdated="September 2025">
      <LegalIntro>
        {companyName} (&ldquo;{companyName},&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; and &ldquo;our&rdquo;) respects your
        privacy and is committed to protecting it through compliance with this Privacy Policy (&ldquo;Privacy
        Policy&rdquo;). This Privacy Policy describes how we collect and use your Personal Information when you visit
        our website or submit your information through our contact forms at{' '}
        <a href={`https://${companyUrl}`} className="text-tan-light transition-colors hover:brightness-125">
          {`https://${companyUrl}`}
        </a>
        .
      </LegalIntro>
      <LegalP>
        Please read this Privacy Policy to understand our policies and practices regarding your Personal Information and
        how we will handle it. If you do not agree with our policies and practices, do not use our website or submit a
        form. By accessing or using our website or submitting a form, you agree and consent to this Privacy Policy.
      </LegalP>
      <LegalP>
        {companyName} may change this Privacy Policy at any time, at its discretion. Your continued use of the website after
        we make changes is deemed to be acceptance of and consent to those changes, so please check the Privacy Policy
        periodically for updates.
      </LegalP>

      <LegalSection title="1. Information We Collect">
        <LegalP>
          We collect personal information that you voluntarily submit through our website and online forms. This may
          include, but not limited to:
        </LegalP>
        <LegalList items={['Name', 'Email address', 'Phone number', 'Any additional information you choose to provide']} />
      </LegalSection>

      <LegalSection title="2. How We Use Your Information">
        <LegalP>We use the information you provide to:</LegalP>
        <LegalList
          items={[
            'Contact you via text, phone call, or email',
            'Respond to your inquiries',
            'Provide information about our venue, events, or services',
          ]}
        />
      </LegalSection>

      <LegalSection title="3. Data Sharing">
        <LegalP>
          We do not sell, rent, or share your personal information with any third-party organizations. All data remains
          strictly confidential.
        </LegalP>
        <LegalP>
          No mobile information will be shared with third parties/affiliates for marketing/promotional purposes.
          Information sharing to subcontractors in support services, such as customer service is permitted. All other use
          case categories exclude text messaging originator opt-in data and consent; this information will not be shared
          with any third parties.
        </LegalP>
      </LegalSection>

      <LegalSection title="4. California Privacy Rights">
        <LegalP>
          If you are a California resident, you have the right to request the following under the California Consumer
          Privacy Act (CCPA):
        </LegalP>
        <LegalList
          items={[
            <>
              <LegalStrong>Access:</LegalStrong> You may request a copy of the personal information we have collected from
              you.
            </>,
            <>
              <LegalStrong>Deletion:</LegalStrong> You may request that we delete the personal information we have
              collected from you.
            </>,
            <>
              <LegalStrong>Disclosure:</LegalStrong> You may request information about the categories and specific pieces of
              personal data we have collected from you.
            </>,
            <>
              <LegalStrong>Opt-out:</LegalStrong> While we do not sell data, you have the right to opt out of any future
              sale of personal information.
            </>,
          ]}
        />
        <LegalP>
          To exercise any of these rights, please use the information provided in the Contact Us section below. We may
          verify your identity before processing your request.
        </LegalP>
      </LegalSection>

      <LegalSection title="5. Data Security">
        <LegalP>
          We take reasonable measures to protect your personal information. However, please note that no method of
          transmission over the internet or method of electronic storage is completely secure.
        </LegalP>
      </LegalSection>

      <LegalSection title="6. Your Consent">
        <LegalP>
          By using our website and submitting your information, you consent to the terms of this Privacy Policy.
        </LegalP>
      </LegalSection>

      <LegalSection title="7. Children&apos;s Information">
        <LegalP>
          Another part of our priority is adding protection for children while using the internet. We encourage parents
          and guardians to observe, participate in, and/or monitor and guide their online activity.
        </LegalP>
        <LegalP>
          {companyName} does not knowingly collect any Personal Identifiable Information from children under the age of 13.
          If you think that your child provided this kind of information on our website, we strongly encourage you to
          contact us immediately and we will do our best efforts to promptly remove such information from our records.
        </LegalP>
      </LegalSection>

      <LegalSection title="8. Online Privacy Policy Only">
        <LegalP>
          This Privacy Policy applies only to our online activities and is valid for visitors to our website with
          regards to the information that you shared. This policy is not applicable to any information collected offline
          or via channels other than this website.
        </LegalP>
      </LegalSection>

      <LegalSection title="9. Changes to This Policy">
        <LegalP>
          We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated
          effective date.
        </LegalP>
      </LegalSection>

      <LegalSection title="10. Contact Us">
        <LegalP>
          If you have any questions or wish to exercise your California privacy rights, please contact:
        </LegalP>
        <LegalP>
          <LegalStrong>{companyName}</LegalStrong>
          <br />
          Email:{' '}
          <a href={`mailto:${companyEmail}`} className="text-tan-light transition-colors hover:brightness-125">
            {companyEmail}
          </a>
        </LegalP>
      </LegalSection>
    </LegalDocumentLayout>
  )
}
